import { Shimmer } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import {
    ConfirmDialog,
    InfoMessage,
    Optional,
    Stack,
    SuccessMessage,
    TextField,
    useNotifications,
    WarningMessage
} from "sprint-common-web";

import { useOrdersModuleApi } from "../../common/useOrdersModuleApi";
import { OrderStatus } from "../../common/types/OrderStatus";

export type CopyOrderOutputsDialogProps = {
    orderId: string;
    isOpen: boolean;
    onClose(): void;
};

export const CopyOrderOutputsDialog = ({ orderId, isOpen, onClose }: CopyOrderOutputsDialogProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [bucket, setBucket] = useState<Optional<string>>();
    const [objectsCount, setObjectsCount] = useState<Optional<number>>();
    const { error, success } = useNotifications();

    const { copyOrderOutputs } = useOrdersModuleApi();

    useEffect(() => {
        if (bucket) {
            navigator.clipboard.writeText(bucket);
        }
    }, [bucket]);

    const onConfirm = async () => {
        setIsLoading(true);

        try {
            const response = await copyOrderOutputs({ orderId });
            setBucket(response.bucket);
            setObjectsCount(response.objectsCount);

            if (response.objectsCount === 0) {
                return;
            }

            success("Order outputs copying started");
        } catch {
            error("Failed to copy order outputs");
        } finally {
            setIsLoading(false);
        }
    };

    const onCancel = () => {
        setBucket(undefined);
        onClose();
    };

    return (
        <ConfirmDialog
            title="Copy order outputs"
            text="Are you sure you want to copy order outputs?"
            hidden={!isOpen}
            confirm={{ onClick: onConfirm, disabled: isLoading || !!bucket || objectsCount === 0 }}
            cancel={{ onClick: onCancel, buttonText: objectsCount !== undefined ? "Close" : "Cancel" }}
            width={400}
        >
            <Stack styles={{ root: { marginBottom: 10 } }}>
                <InfoMessage
                    message={
                        'Please note that after running "Select Video" or "Reprocess", the output files will not be copied automatically. The copy action must be executed again to process the newly generated files.'
                    }
                />
            </Stack>
            <Stack styles={{ root: { marginBottom: 10 } }}>
                {isLoading && (
                    <>
                        <Shimmer></Shimmer>
                        <Shimmer></Shimmer>
                    </>
                )}
                {objectsCount === 0 && !isLoading && (
                    <WarningMessage message={"There are no outputs to copy for this order."}></WarningMessage>
                )}
                {bucket && (
                    <>
                        <SuccessMessage
                            message={`The order outputs copying is started and the files will be available in the bucket listed below once the process is completed. Number of files to copy: ${objectsCount}. The bucket name has been copied to your clipboard for your convenience.`}
                        ></SuccessMessage>
                        <TextField value={bucket} multiline={true} disabled={true}></TextField>
                    </>
                )}
            </Stack>
        </ConfirmDialog>
    );
};

export const canCopyOrderOutputs = (status: OrderStatus) =>
    [
        OrderStatus.Encoded,
        OrderStatus.DeliveryApproved,
        OrderStatus.DeliveryCompleted,
        OrderStatus.DeliveryFailed,
        OrderStatus.DeliveryError,
        OrderStatus.Rejected
    ].includes(status);
